import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { axios } from '../../libs/axios';
import { useTranslation } from '../../i18n/I18nContext';
import { fmtInt, fmtScore, fmtNum } from './charts';

function DrilldownPanel({ userNo, paramsProfile }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/match/lawyers/${userNo}/drilldown`, {
          params: { paramsProfile },
        });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(t('match.drilldownError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userNo, paramsProfile, t]);

  if (loading) return <div className="p-2">{t('common.loading')}</div>;
  if (error) return <div className="p-2 text-danger">{error}</div>;
  if (!data) return null;

  const h = data.header || {};
  const negCount = h.responseExclusionCounts?.negative_response || 0;

  return (
    <div className="match-drilldown p-3" style={{ background: '#f8f9fa' }}>
      <p className="mb-2"><strong>{h.formula}</strong></p>
      {h.loadRaw > 1 && (
        <p className="mb-1 small text-warning">{t('match.loadOverCapacity', { L: fmtNum(h.loadRaw, 4) })}</p>
      )}
      {(h.summaryLines || []).map((line) => (
        <p key={line} className="mb-1 text-muted small">{line}</p>
      ))}
      {negCount > 3 && (
        <p className="alert alert-warning py-1 small">
          {t('match.negativeResponseWarning', { count: negCount })}
        </p>
      )}

      <h6>{t('match.projectList')}</h6>
      <div className="match-table-scroll">
        <table className="table table-sm table-bordered match-drilldown-table">
          <thead>
            <tr>
              <th>{t('match.projectNo')}</th>
              <th>type</th>
              <th>status</th>
              <th>{t('match.col.load')}</th>
              <th>{t('match.col.speed')}</th>
              <th>{t('match.col.sla')}</th>
              <th className="match-col-num">age (d)</th>
              <th className="match-col-num">{t('match.col.bizHours')}</th>
              <th className="match-col-num">{t('match.col.calHours')}</th>
              <th>{t('match.col.deadline')}</th>
              <th className="match-col-num">{t('match.col.overage')}</th>
              <th className="match-col-num">{t('match.col.ageWeight')}</th>
              <th>{t('match.col.weekend')}</th>
            </tr>
          </thead>
          <tbody>
            {(data.projects || []).map((p) => (
              <tr key={p.project_no || `${p.project_type}-${p.status}-${p.ageDays}`}>
                <td>
                  {p.project_no ? (
                    <Link to={`/projects/${p.project_no}`}>{p.project_no}</Link>
                  ) : '—'}
                </td>
                <td className="match-col-type">{p.project_type}</td>
                <td>{p.status}</td>
                <td>{p.load}{p.loadReason ? ` / ${p.loadReason}` : ''}</td>
                <td>{p.speed}{p.speedReason ? ` / ${p.speedReason}` : ''}</td>
                <td>
                  {p.sla}
                  {p.slaReason ? ` / ${p.slaReason}` : ''}
                  {p.weekend_crossed ? ` (${t('match.weekendCrossed')})` : ''}
                </td>
                <td className="match-col-num">{p.ageDays != null ? fmtScore(p.ageDays) : '—'}</td>
                <td className="match-col-num">{p.businessHoursElapsed != null ? fmtScore(p.businessHoursElapsed) : '—'}</td>
                <td className="match-col-num">{p.calendarHoursElapsed != null ? fmtScore(p.calendarHoursElapsed) : '—'}</td>
                <td className="small">
                  {p.deadlineBoundary
                    ? new Date(p.deadlineBoundary).toLocaleString()
                    : (p.deadlineMoment ? new Date(p.deadlineMoment).toLocaleString() : '—')}
                  {p.task_date && (
                    <div className="text-muted">{t('match.taskDateUnused')}: {new Date(p.task_date).toLocaleString()}</div>
                  )}
                  {p.storedDeadline && (
                    <div className="text-muted">{t('match.storedDeadlineUnused')}: {new Date(p.storedDeadline).toLocaleString()}</div>
                  )}
                </td>
                <td className="match-col-num">{p.overageHours != null ? fmtScore(p.overageHours) : '—'}</td>
                <td className="match-col-num">{p.ageWeight != null ? fmtScore(p.ageWeight) : '—'}</td>
                <td>{p.weekend_crossed ? t('common.yes') : t('common.no')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row">
        <div className="col-md-3">
          <h6>{t('match.speedContributors')} (k={data.speed?.contributors?.length || 0})</h6>
          <ul className="small">
            {(data.speed?.contributors || []).slice(0, 10).map((s) => (
              <li key={s.project_no}>{s.project_no}: {fmtScore(s.responseHours)}h</li>
            ))}
          </ul>
        </div>
        <div className="col-md-3">
          <h6>{t('match.slaBreaches')}</h6>
          <ul className="small">
            {(data.speed?.breaches || []).slice(0, 10).map((s) => (
              <li key={s.project_no}>{s.project_no}: {fmtScore(s.responseHours)}h</li>
            ))}
          </ul>
        </div>
        <div className="col-md-3">
          <h6>{t('match.responseOutliers')}</h6>
          <ul className="small">
            {(data.speed?.exclusions || []).filter((s) => s.reason === 'response_outlier').slice(0, 10).map((s) => (
              <li key={s.project_no}>{s.project_no}</li>
            ))}
          </ul>
        </div>
        <div className="col-md-3">
          <h6>{t('match.slaExclusions')}</h6>
          <ul className="small">
            {(data.speed?.exclusions || []).slice(0, 10).map((s) => (
              <li key={s.project_no}>{s.project_no}: {s.reason}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const COLUMN_GROUP_KEYS = {
  expand: 'identity',
  name: 'identity',
  Uq: 'load',
  N7: 'load',
  N14: 'load',
  flow: 'load',
  waiting_raw: 'load',
  Act: 'load',
  assigned90: 'load',
  completed: 'load',
  L: 'load',
  E: 'components',
  R: 'components',
  S: 'components',
  respMedianH: 'components',
  Q: 'components',
  A: 'multipliers',
  v: 'multipliers',
  D: 'multipliers',
  sigma: 'selection',
  softmaxP: 'selection',
  inclusionP: 'selection',
  declaredSelectedCount: 'selection',
  maturity: 'selection',
  quotaEligible: 'selection',
  coverageMultiplier: 'multipliers',
  C: 'multipliers',
  slot: 'selection',
  n: 'selection',
  k: 'selection',
  rank: 'selection',
};

const PARAM_TOOLTIP_KEYS = {
  E: 'toggleE',
  R: 'toggleR',
  S: 'toggleS',
  A: 'toggleA',
  D: 'toggleD',
  Uq: 'toggleBacklogLoad',
};

const SORTABLE_KEYS = new Set([
  'Uq', 'N7', 'N14', 'flow', 'waiting_raw', 'Act', 'assigned90', 'completed',
  'L', 'E', 'R', 'S', 'respMedianH', 'Q', 'A', 'v', 'D', 'sigma', 'softmaxP', 'inclusionP',
  'declaredSelectedCount', 'maturity', 'coverageMultiplier', 'C', 'n', 'k', 'exclusionCount',
]);

function columnGroup(key) {
  return COLUMN_GROUP_KEYS[key] || 'selection';
}

function shortLabel(col, t) {
  if (col.key === 'expand') return '';
  const shortKey = `match.short.${col.key}`;
  const translated = t(shortKey);
  if (translated && translated !== shortKey) {
    return translated;
  }
  return col.label;
}

function columnTooltip(col, t) {
  if (col.info) {
    return `${col.label} (${t('match.informational')})`;
  }
  const colTipKey = `match.colTooltip.${col.key}`;
  const colTip = t(colTipKey);
  if (colTip && colTip !== colTipKey) {
    return colTip;
  }
  const paramKey = PARAM_TOOLTIP_KEYS[col.key];
  if (paramKey) {
    const what = t(`match.paramTooltip.${paramKey}.what`);
    const direction = t(`match.paramTooltip.${paramKey}.direction`);
    if (what && what !== `match.paramTooltip.${paramKey}.what`) {
      return direction && direction !== `match.paramTooltip.${paramKey}.direction`
        ? `${what} ${direction}`
        : what;
    }
  }
  if (col.key === 'softmaxP' || col.key === 'inclusionP') {
    return col.label;
  }
  return col.label;
}

function cellClass(col, row) {
  if (col.key === 'expand') return 'match-col-expand';
  if (col.key === 'name') return 'match-col-name';
  if (col.key === 'L' && (row?.loadRaw ?? row?.L) > 1) return 'match-col-num match-load-over';
  if (col.fmtType === 'int') return 'match-col-num';
  return 'match-col-num';
}

function formatLoadCell(val, row, t) {
  const L = row.loadRaw ?? val;
  if (L > 1) {
    return fmtNum(L, 4);
  }
  return fmtScore(val);
}

function formatCell(col, val, row, t, badgeHints) {
  if (col.fmt) return col.fmt(val, row, t, badgeHints);
  if (col.key === 'L') return formatLoadCell(val, row, t);
  if (col.fmtType === 'int') return fmtInt(val);
  if (col.fmtType === 'score') return fmtScore(val);
  return val ?? '—';
}

function cellTitle(col, row, t) {
  if (col.key === 'L' && (row.loadRaw ?? row.L) > 1) {
    return t('match.loadOverCapacity', { L: fmtNum(row.loadRaw ?? row.L, 4) });
  }
  if (typeof col.title === 'function') return col.title(row);
  if (typeof col.title === 'string') return col.title;
  return undefined;
}

function compareRows(a, b, sortKey, sortDir) {
  if (sortKey === 'declaredSelectedCount') {
    const countDiff = (a.declaredSelectedCount ?? 0) - (b.declaredSelectedCount ?? 0);
    if (countDiff !== 0) {
      return sortDir === 'asc' ? countDiff : -countDiff;
    }
    const rankA = (a.declaredSelectedRanks || []).join(',');
    const rankB = (b.declaredSelectedRanks || []).join(',');
    const rankDiff = rankA.localeCompare(rankB);
    return sortDir === 'asc' ? rankDiff : -rankDiff;
  }
  const av = a[sortKey];
  const bv = b[sortKey];
  const aNum = typeof av === 'number' ? av : Number(av);
  const bNum = typeof bv === 'number' ? bv : Number(bv);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
  }
  const aStr = String(av ?? '');
  const bStr = String(bv ?? '');
  const cmp = aStr.localeCompare(bStr);
  return sortDir === 'asc' ? cmp : -cmp;
}

function buildGroupHeaderRow(columns, t) {
  const groups = [];
  columns.forEach((col) => {
    const groupKey = columnGroup(col.key);
    const last = groups[groups.length - 1];
    if (last && last.key === groupKey) {
      last.span += 1;
      return;
    }
    groups.push({
      key: groupKey,
      span: 1,
      label: t(`match.tableGroup.${groupKey}`),
    });
  });
  return groups;
}

export function LawyerResultsTable({
  rows = [],
  extraColumns = [],
  paramsProfile,
  keyField = 'user_no',
  badgeHints = null,
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const baseColumns = [
    { key: 'expand', label: '', sortable: false },
    { key: 'name', label: t('match.colName'), sortable: false },
    { key: 'Uq', label: t('match.col.Uq'), fmtType: 'int', sortable: true },
    { key: 'N7', label: t('match.col.N7'), fmtType: 'int', sortable: true },
    { key: 'N14', label: t('match.col.N14'), fmtType: 'int', sortable: true },
    { key: 'flow', label: t('match.col.flow'), fmtType: 'score', sortable: true },
    { key: 'waiting_raw', label: t('match.col.waitingRaw'), fmtType: 'int', info: true, sortable: true },
    { key: 'Act', label: t('match.col.Act'), fmtType: 'int', info: true, sortable: true },
    { key: 'assigned90', label: t('match.col.assigned90'), fmtType: 'int', info: true, sortable: true },
    { key: 'completed', label: t('match.col.completed'), fmtType: 'int', info: true, sortable: true },
    { key: 'L', label: t('match.col.L'), fmtType: 'score', sortable: true },
    ...extraColumns.map((col) => ({
      ...col,
      sortable: col.sortable !== false && (SORTABLE_KEYS.has(col.key) || col.key === 'declaredSelectedCount'),
    })),
  ];

  const groupHeaderRow = useMemo(
    () => buildGroupHeaderRow(baseColumns, t),
    [baseColumns, t],
  );

  const displayRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [rows, sortKey, sortDir]);

  const toggle = (userNo) => {
    setExpanded((prev) => (prev === userNo ? null : userNo));
  };

  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(col.key);
    setSortDir(col.key === 'name' ? 'asc' : 'desc');
  };

  const sortIndicator = (col) => {
    if (sortKey !== col.key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="match-table-scroll">
      <table className="table table-sm table-striped match-lawyer-table">
        <thead>
          <tr className="match-table-group-row">
            {groupHeaderRow.map((group) => (
              <th
                key={group.key}
                colSpan={group.span}
                className={`match-table-group-header match-table-group-${group.key}`}
              >
                {group.label}
              </th>
            ))}
          </tr>
          <tr>
            {baseColumns.map((col) => {
              const group = columnGroup(col.key);
              const isGroupStart = baseColumns.findIndex(
                (c) => columnGroup(c.key) === group,
              ) === baseColumns.indexOf(col);
              return (
                <th
                  key={col.key}
                  className={`${cellClass(col)}${isGroupStart && group !== 'identity' ? ' match-col-group-start' : ''}${col.sortable ? ' match-col-sortable' : ''}`}
                  title={columnTooltip(col, t)}
                  onClick={col.sortable ? () => handleSort(col) : undefined}
                  onKeyDown={col.sortable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSort(col);
                    }
                  } : undefined}
                  tabIndex={col.sortable ? 0 : undefined}
                  role={col.sortable ? 'button' : undefined}
                  aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span className="match-col-header-label">
                    {col.key === 'expand' ? '' : shortLabel(col, t)}
                    {sortIndicator(col)}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row) => (
            <React.Fragment key={row[keyField]}>
              <tr>
                {baseColumns.map((col) => {
                  if (col.key === 'expand') {
                    return (
                      <td key={col.key} className="match-col-expand">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => toggle(row[keyField])}
                          aria-label={t('match.expandDrilldown')}
                        >
                          {expanded === row[keyField] ? '▼' : '▶'}
                        </button>
                      </td>
                    );
                  }
                  const val = row[col.key];
                  const title = cellTitle(col, row, t);
                  return (
                    <td key={col.key} className={cellClass(col, row)} title={title}>
                      {formatCell(col, val, row, t, badgeHints)}
                    </td>
                  );
                })}
              </tr>
              {expanded === row[keyField] && (
                <tr className="match-drilldown-row">
                  <td colSpan={baseColumns.length}>
                    {row.maturityTerms && (
                      <p className="small text-muted mb-2 px-3 pt-2">
                        {t('match.maturityTerms', {
                          done: fmtScore(row.maturityTerms.done),
                          rating: fmtScore(row.maturityTerms.rating),
                          recent: fmtScore(row.maturityTerms.recent),
                        })}
                      </p>
                    )}
                    <DrilldownPanel userNo={row[keyField]} paramsProfile={paramsProfile} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
